import type {
  Product,
  Order,
  Customer,
  Coupon,
  Store,
  StoreStats,
  PaymentSettings,
  CartItem,
  Address,
  CheckoutCalculation,
  ApiResponse,
  JjsPaymentInitRequest,
  JjsPaymentInitResponse,
  ShipStationCatalogIngestSummary,
  ShipStationFamilyProposal,
  ShipStationMapping,
  ShipStationMappingsListResponse,
  ShipStationMappingsQuery,
  ShipStationInventorySyncSummary,
  ShipStationFulfillmentRatesRequest,
  ShipStationFulfillmentRatesResponse,
  ShipStationValidateAddressInput,
  ShipStationAddressValidationResult,
  ShipStationCreateFulfillmentBatchRequest,
  ShipStationFulfillmentBatchResponse,
  ShipStationFulfillmentLabel,
  ShipStationCreateManifestRequest,
  ShipStationManifestResponse,
  ShipStationFulfillmentCompleteRequest,
  ShipStationFulfillmentCompleteResult,
  MetafieldDefinition,
  EntityMetafield,
} from '../types/index.js';
import type {
  IntegrationsListResponse,
  ShipStationIntegrationResponse,
  ShipStationIntegrationUpdateRequest,
  ShipStationTestResponse,
  CartRoverIntegrationResponse,
  CartRoverIntegrationUpdateRequest,
  CartRoverTestResponse,
  CartRoverInventorySyncSummary,
} from '../integrations/types.js';
import type { ShipStationExportSummary } from '../integrations/shipstation/order-export.js';
import type { SkuMappingRecord } from '../integrations/shipstation/merge.js';
import type { VariantFamilyProposal } from '../integrations/shipstation/variant-mapper.js';
import type { StoreApiConfig } from './config.js';

const MAX_MAPPING_UPDATES_PER_REQUEST = 100;

interface ShipStationMappingsListPayload {
  mappings: SkuMappingRecord[];
  total?: number;
  unmappedTotal?: number;
  mappedTotal?: number;
  limit?: number;
  offset?: number;
}

interface ShipStationFamiliesPayload {
  families: VariantFamilyProposal[];
}

interface ShipStationMappingUpdatePayload {
  ssSku: string;
  productId?: string | null;
  productVariantId?: string | null;
  sellable?: boolean;
}

function toShipStationMapping(record: SkuMappingRecord): ShipStationMapping {
  return {
    shipstationSku: record.ssSku,
    productVariantId: record.productVariantId ?? undefined,
    sellable: record.sellable,
  };
}

function toMappingUpdatePayload(mapping: ShipStationMapping): ShipStationMappingUpdatePayload {
  return {
    ssSku: mapping.shipstationSku,
    productVariantId: mapping.productVariantId ?? null,
    sellable: mapping.sellable,
  };
}

export class StoreApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'StoreApiError';
  }
}

export interface CheckoutProcessResult {
  orderId: string;
  total: number;
  order?: Order;
}

export interface CouponValidationResult {
  valid: boolean;
  discount: number;
  message?: string;
  coupon?: Coupon;
}

export interface ProductImageUploadResult {
  id: string;
  url: string;
  storedUrl: string;
  contentType: string;
  size: number;
}

export type StoreMediaStatus = 'processing' | 'ready' | 'failed';

export interface StoreMediaRecord {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: StoreMediaStatus;
  url: string;
  storageKey: string;
  variants?: unknown;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StoreMediaListResult {
  items: StoreMediaRecord[];
  nextCursor?: string;
}

export interface StoreMediaPresignRequest {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface StoreMediaPresignResult {
  mediaId: string;
  storageKey: string;
  uploadUrl: string;
  expiresAt: string;
  headers: Record<string, string>;
  mode: 'r2-presign' | 'worker-fallback';
  registerRequired: boolean;
}

export interface StoreMediaRegisterRequest {
  mediaId: string;
  storageKey: string;
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
}

/** Bound fetch avoids "Illegal invocation" when calling window.fetch without `this`. */
function defaultFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return globalThis.fetch(input, init);
}

export class StoreApiClient {
  private readonly fetchFn: typeof fetch;

  constructor(private readonly config: StoreApiConfig) {
    this.fetchFn = config.fetch ?? defaultFetch;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    requireAuth = false
  ): Promise<T> {
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');
    if (this.config.authToken) {
      headers.set('Authorization', `Bearer ${this.config.authToken}`);
    }

    const response = await this.fetchFn(`${this.config.baseUrl}${path}`, {
      ...options,
      headers,
    });

    const payload = (await response.json()) as ApiResponse<T> & { error?: string };

    if (!response.ok) {
      throw new StoreApiError(payload.error ?? response.statusText, response.status);
    }

    if (payload.error) {
      throw new StoreApiError(payload.error, response.status);
    }

    return payload.data as T;
  }

  private storePath(suffix: string): string {
    return `/api/v1/stores/${encodeURIComponent(this.config.storeId)}${suffix}`;
  }

  async getProducts(params?: { q?: string; category?: string }): Promise<Product[]> {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.category) search.set('category', params.category);
    const qs = search.toString();
    return this.request<Product[]>(
      `${this.storePath('/products')}${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true
    );
  }

  async getProduct(productId: string): Promise<Product> {
    return this.request<Product>(this.storePath(`/products/${encodeURIComponent(productId)}`), {
      method: 'GET',
    });
  }

  async createProduct(product: Product): Promise<Product> {
    return this.request<Product>(this.storePath('/products'), {
      method: 'POST',
      body: JSON.stringify(product),
    }, true);
  }

  async updateProduct(productId: string, product: Partial<Product>): Promise<Product> {
    return this.request<Product>(
      this.storePath(`/products/${encodeURIComponent(productId)}`),
      { method: 'PUT', body: JSON.stringify(product) },
      true
    );
  }

  async deleteProduct(productId: string): Promise<void> {
    await this.request<{ deleted: boolean }>(
      this.storePath(`/products/${encodeURIComponent(productId)}`),
      { method: 'DELETE' },
      true
    );
  }

  async uploadProductImages(files: File[]): Promise<ProductImageUploadResult[]> {
    if (files.length === 0) {
      throw new StoreApiError('No files to upload', 400);
    }

    const formData = new FormData();
    for (const file of files) {
      formData.append('images', file, file.name);
    }

    const headers = new Headers();
    if (this.config.authToken) {
      headers.set('Authorization', `Bearer ${this.config.authToken}`);
    }

    const response = await this.fetchFn(`${this.config.baseUrl}${this.storePath('/product-images')}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const payload = (await response.json()) as {
      data?: ProductImageUploadResult[];
      error?: string;
      errors?: string[];
    };

    if (!response.ok) {
      throw new StoreApiError(payload.error ?? response.statusText, response.status);
    }

    if (!payload.data || payload.data.length === 0) {
      throw new StoreApiError(payload.error ?? 'Upload failed', response.status);
    }

    return payload.data;
  }

  async listMedia(params?: {
    limit?: number;
    cursor?: string;
    status?: StoreMediaStatus;
  }): Promise<StoreMediaListResult> {
    const search = new URLSearchParams();
    if (params?.limit !== undefined) {
      search.set('limit', String(params.limit));
    }
    if (params?.cursor) {
      search.set('cursor', params.cursor);
    }
    if (params?.status) {
      search.set('status', params.status);
    }
    const qs = search.toString();
    const path = `${this.storePath('/media')}${qs ? `?${qs}` : ''}`;
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    if (this.config.authToken) {
      headers.set('Authorization', `Bearer ${this.config.authToken}`);
    }

    const response = await this.fetchFn(`${this.config.baseUrl}${path}`, {
      method: 'GET',
      headers,
    });
    const payload = (await response.json()) as ApiResponse<StoreMediaRecord[]> & {
      nextCursor?: string;
      error?: string;
    };

    if (!response.ok) {
      throw new StoreApiError(payload.error ?? response.statusText, response.status);
    }
    if (payload.error) {
      throw new StoreApiError(payload.error, response.status);
    }

    return {
      items: payload.data ?? [],
      nextCursor: payload.nextCursor,
    };
  }

  async presignMediaUpload(body: StoreMediaPresignRequest): Promise<StoreMediaPresignResult> {
    return this.request<StoreMediaPresignResult>(
      this.storePath('/media/presign'),
      { method: 'POST', body: JSON.stringify(body) },
      true
    );
  }

  async registerMedia(body: StoreMediaRegisterRequest): Promise<StoreMediaRecord> {
    return this.request<StoreMediaRecord>(
      this.storePath('/media/register'),
      { method: 'POST', body: JSON.stringify(body) },
      true
    );
  }

  async getMedia(mediaId: string): Promise<StoreMediaRecord> {
    return this.request<StoreMediaRecord>(
      this.storePath(`/media/${encodeURIComponent(mediaId)}`),
      { method: 'GET' },
      true
    );
  }

  /** Worker-fallback presign: PUT bytes to the authenticated content route. */
  async putMediaContent(
    mediaId: string,
    body: Uint8Array | ArrayBuffer,
    mimeType: string
  ): Promise<void> {
    const headers = new Headers();
    headers.set('Content-Type', mimeType);
    if (this.config.authToken) {
      headers.set('Authorization', `Bearer ${this.config.authToken}`);
    }

    const requestBody: BodyInit =
      body instanceof Uint8Array ? new Uint8Array(body) : body;

    const response = await this.fetchFn(
      `${this.config.baseUrl}${this.storePath(`/media/${encodeURIComponent(mediaId)}/content`)}`,
      {
        method: 'PUT',
        headers,
        body: requestBody,
      }
    );

    if (!response.ok) {
      let message = response.statusText;
      try {
        const payload = (await response.json()) as { error?: string };
        if (payload.error) {
          message = payload.error;
        }
      } catch {
        const text = await response.text().catch(() => '');
        if (text) {
          message = text;
        }
      }
      throw new StoreApiError(message, response.status);
    }
  }

  async deleteMedia(mediaId: string): Promise<void> {
    await this.request<{ data: { id: string; deleted: boolean } }>(
      this.storePath(`/media/${encodeURIComponent(mediaId)}`),
      { method: 'DELETE' },
      true
    );
  }

  async getOrders(params?: { shipstationExportStatus?: string }): Promise<Order[]> {
    const search = new URLSearchParams();
    if (params?.shipstationExportStatus) {
      search.set('shipstationExportStatus', params.shipstationExportStatus);
    }
    const qs = search.toString();
    return this.request<Order[]>(
      `${this.storePath('/orders')}${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true
    );
  }

  async getOrder(orderId: string): Promise<Order> {
    return this.request<Order>(
      this.storePath(`/orders/${encodeURIComponent(orderId)}`),
      { method: 'GET' },
      true
    );
  }

  async updateOrder(orderId: string, order: Partial<Order>): Promise<Order> {
    return this.request<Order>(
      this.storePath(`/orders/${encodeURIComponent(orderId)}`),
      { method: 'PUT', body: JSON.stringify(order) },
      true
    );
  }

  async getCustomers(): Promise<Customer[]> {
    return this.request<Customer[]>(this.storePath('/customers'), { method: 'GET' }, true);
  }

  async getCoupons(): Promise<Coupon[]> {
    return this.request<Coupon[]>(this.storePath('/coupons'), { method: 'GET' }, true);
  }

  async createCoupon(coupon: Coupon): Promise<Coupon> {
    return this.request<Coupon>(
      this.storePath('/coupons'),
      { method: 'POST', body: JSON.stringify(coupon) },
      true
    );
  }

  async updateCoupon(couponId: string, coupon: Partial<Coupon>): Promise<Coupon> {
    return this.request<Coupon>(
      this.storePath(`/coupons/${encodeURIComponent(couponId)}`),
      { method: 'PUT', body: JSON.stringify(coupon) },
      true
    );
  }

  async deleteCoupon(couponId: string): Promise<void> {
    await this.request<{ deleted: boolean }>(
      this.storePath(`/coupons/${encodeURIComponent(couponId)}`),
      { method: 'DELETE' },
      true
    );
  }

  async getStore(): Promise<Store> {
    return this.request<Store>(this.storePath(''), { method: 'GET' }, true);
  }

  async updateStore(store: Partial<Store>): Promise<Store> {
    return this.request<Store>(
      this.storePath(''),
      { method: 'PUT', body: JSON.stringify(store) },
      true
    );
  }

  async getStoreStats(): Promise<StoreStats> {
    return this.request<StoreStats>(this.storePath('/stats'), { method: 'GET' }, true);
  }

  async getPaymentSettings(): Promise<PaymentSettings> {
    return this.request<PaymentSettings>(
      this.storePath('/settings/payment'),
      { method: 'GET' },
      true
    );
  }

  async updatePaymentSettings(
    settings: Partial<PaymentSettings>
  ): Promise<PaymentSettings> {
    return this.request<PaymentSettings>(
      this.storePath('/settings/payment'),
      { method: 'PUT', body: JSON.stringify(settings) },
      true
    );
  }

  async checkoutCalculate(body: {
    items: CartItem[];
    couponCode?: string;
    shippingAddress?: Partial<Address>;
    shippingMethodId?: string;
  }): Promise<CheckoutCalculation> {
    return this.request<CheckoutCalculation>(
      this.storePath('/checkout/calculate'),
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  async checkoutProcess(body: {
    items: CartItem[];
    couponCode?: string;
    shippingAddress?: Partial<Address>;
    shippingMethod?: string;
    email?: string;
  }): Promise<CheckoutProcessResult> {
    return this.request<CheckoutProcessResult>(
      this.storePath('/checkout/process'),
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  async validateCoupon(code: string, orderAmount?: number): Promise<CouponValidationResult> {
    return this.request<CouponValidationResult>(
      this.storePath('/coupons/validate'),
      {
        method: 'POST',
        body: JSON.stringify({ code, orderAmount }),
      }
    );
  }

  /** Phase 2: server-side JJS wallet.php + redirect URL generation */
  async initJjsPayment(body: JjsPaymentInitRequest): Promise<JjsPaymentInitResponse> {
    return this.request<JjsPaymentInitResponse>(
      this.storePath('/checkout/payment/jjs/init'),
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  /** Lists integration modules and per-store capability slot assignments. */
  async getIntegrations(): Promise<IntegrationsListResponse> {
    return this.request<IntegrationsListResponse>(
      this.storePath('/integrations'),
      { method: 'GET' },
      true
    );
  }

  async getExtensions(): Promise<any> {
    return this.request<any>(
      this.storePath('/integrations/extensions'),
      { method: 'GET' },
      true
    );
  }

  async installExtension(manifestJson: any): Promise<any> {
    return this.request<any>(
      this.storePath('/integrations/extensions'),
      { method: 'POST', body: JSON.stringify({ manifestJson }) },
      true
    );
  }

  /** Phase 5: Load persisted ShipStation integration (credentials redacted). */
  async getShipStationIntegration(): Promise<ShipStationIntegrationResponse> {
    return this.request<ShipStationIntegrationResponse>(
      this.storePath('/integrations/shipstation'),
      { method: 'GET' },
      true
    );
  }

  /** Phase 5: Save ShipStation credentials, config, and provider slots. */
  async updateShipStationIntegration(
    body: ShipStationIntegrationUpdateRequest
  ): Promise<ShipStationIntegrationResponse> {
    return this.request<ShipStationIntegrationResponse>(
      this.storePath('/integrations/shipstation'),
      { method: 'PUT', body: JSON.stringify(body) },
      true
    );
  }

  /** Phase 5: Verify ShipStation V1 API connectivity. */
  async testShipStationConnection(): Promise<ShipStationTestResponse> {
    return this.request<ShipStationTestResponse>(
      this.storePath('/integrations/shipstation/test'),
      { method: 'POST' },
      true
    );
  }

  /** Load persisted CartRover integration (credentials redacted). */
  async getCartRoverIntegration(): Promise<CartRoverIntegrationResponse> {
    return this.request<CartRoverIntegrationResponse>(
      this.storePath('/integrations/cartrover'),
      { method: 'GET' },
      true
    );
  }

  /** Save CartRover credentials, config, and provider slots. */
  async updateCartRoverIntegration(
    body: CartRoverIntegrationUpdateRequest
  ): Promise<CartRoverIntegrationResponse> {
    return this.request<CartRoverIntegrationResponse>(
      this.storePath('/integrations/cartrover'),
      { method: 'PUT', body: JSON.stringify(body) },
      true
    );
  }

  /** Verify CartRover Merchant API connectivity. */
  async testCartRoverConnection(): Promise<CartRoverTestResponse> {
    return this.request<CartRoverTestResponse>(
      this.storePath('/integrations/cartrover/test'),
      { method: 'POST' },
      true
    );
  }

  /** Sync one CartRover inventory batch (call until hasMore is false). */
  async syncCartRoverInventory(
    options?: { offset?: number; page?: number }
  ): Promise<CartRoverInventorySyncSummary> {
    return this.request<CartRoverInventorySyncSummary>(
      this.storePath('/integrations/cartrover/sync'),
      {
        method: 'POST',
        body: JSON.stringify(options ?? {}),
      },
      true
    );
  }

  /** Sync all CartRover inventory pages into D1 variant quantities (client-driven batching). */
  async syncCartRoverInventoryAll(): Promise<CartRoverInventorySyncSummary> {
    let page = 1;
    let totalSynced = 0;
    let updatedCount = 0;
    let totalAvailable = 0;
    let hasMore = true;

    while (hasMore) {
      const summary = await this.syncCartRoverInventory({ page });
      totalSynced += summary.totalSynced;
      updatedCount += summary.updatedCount;
      totalAvailable = summary.totalAvailable;
      hasMore = summary.hasMore;
      page = summary.offset;
    }

    return {
      totalSynced,
      updatedCount,
      offset: page,
      totalAvailable,
      hasMore: false,
    };
  }

  async getWebhooks(): Promise<any> {
    return this.request<any>(
      this.storePath('/integrations/webhooks'),
      { method: 'GET' },
      true
    );
  }

  async createWebhook(url: string, topics: string[]): Promise<any> {
    return this.request<any>(
      this.storePath('/integrations/webhooks'),
      { method: 'POST', body: JSON.stringify({ url, topics, active: true }) },
      true
    );
  }

  async getDeliveryLog(): Promise<any> {
    return this.request<any>(
      this.storePath('/integrations/deliveries'),
      { method: 'GET' },
      true
    );
  }

  async retryDelivery(deliveryId: string): Promise<any> {
    return this.request<any>(
      this.storePath(`/integrations/deliveries/${encodeURIComponent(deliveryId)}/retry`),
      { method: 'POST' },
      true
    );
  }

  async getThemeProfiles(): Promise<any> {
    return this.request<any>(
      this.storePath('/integrations/themes'),
      { method: 'GET' },
      true
    );
  }

  async createThemeProfile(name: string, tokens: Record<string, string>): Promise<any> {
    return this.request<any>(
      this.storePath('/integrations/themes'),
      { method: 'POST', body: JSON.stringify({ name, is_active: true, tokens }) },
      true
    );
  }

  /** Bulk V1 export of eligible orders to ShipStation. */
  async exportShipStationOrders(body?: { orderIds?: string[] }): Promise<ShipStationExportSummary> {
    return this.request<ShipStationExportSummary>(
      this.storePath('/integrations/shipstation/orders/export'),
      { method: 'POST', body: JSON.stringify(body ?? {}) },
      true
    );
  }

  /** Phase 3: Ingest one page of ShipStation catalog (call until hasMore is false). */
  async ingestShipStationCatalog(
    options?: { page?: number }
  ): Promise<ShipStationCatalogIngestSummary> {
    return this.request<ShipStationCatalogIngestSummary>(
      this.storePath('/integrations/shipstation/catalog/ingest'),
      {
        method: 'POST',
        body: JSON.stringify(options ?? {}),
      },
      true
    );
  }

  /** Phase 3: Ingest all catalog pages (client-driven pagination). */
  async ingestShipStationCatalogAll(): Promise<ShipStationCatalogIngestSummary> {
    let page = 1;
    let totalIngested = 0;
    let newSkus = 0;
    let updatedSkus = 0;
    let totalPages = 1;
    let hasMore = true;

    while (hasMore) {
      const summary = await this.ingestShipStationCatalog({ page });
      totalIngested += summary.totalIngested;
      newSkus += summary.newSkus;
      updatedSkus += summary.updatedSkus;
      totalPages = summary.totalPages;
      hasMore = summary.hasMore;
      page = summary.page + 1;
    }

    return {
      totalIngested,
      newSkus,
      updatedSkus,
      page: totalPages,
      totalPages,
      hasMore: false,
    };
  }

  /** Phase 3: Propose product families from ingested catalog. */
  async proposeShipStationFamilies(): Promise<ShipStationFamilyProposal[]> {
    const payload = await this.request<ShipStationFamiliesPayload>(
      this.storePath('/integrations/shipstation/catalog/propose-families'),
      { method: 'POST' },
      true
    );
    return (payload.families ?? []).map((family) => ({
      peptideName: family.peptideName,
      skus: family.skus,
    }));
  }

  /** Phase 3: Get SKU mappings (paginated when limit/offset provided). */
  async getShipStationMappings(
    params?: ShipStationMappingsQuery
  ): Promise<ShipStationMappingsListResponse> {
    const search = new URLSearchParams();
    if (params?.unmappedOnly) search.set('unmappedOnly', 'true');
    if (params?.mappedOnly) search.set('mappedOnly', 'true');
    if (params?.sellableOnly) search.set('sellableOnly', 'true');
    if (params?.productId) search.set('productId', params.productId);
    if (params?.search?.trim()) search.set('search', params.search.trim());
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    if (params?.offset !== undefined) search.set('offset', String(params.offset));
    const qs = search.toString();
    const payload = await this.request<ShipStationMappingsListPayload>(
      `${this.storePath('/integrations/shipstation/catalog/mappings')}${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true
    );
    const mappings = (payload.mappings ?? []).map(toShipStationMapping);
    const total = payload.total ?? mappings.length;
    return {
      mappings,
      total,
      unmappedTotal: payload.unmappedTotal ?? mappings.filter((m) => !m.productVariantId).length,
      mappedTotal: payload.mappedTotal ?? mappings.filter((m) => !!m.productVariantId).length,
      limit: payload.limit ?? total,
      offset: payload.offset ?? 0,
    };
  }

  /** Phase 3: Update SKU mappings (batched to respect API max 100 per request). */
  async updateShipStationMappings(body: {
    mappings: ShipStationMapping[];
  }): Promise<{ updated: number }> {
    let updated = 0;

    for (let i = 0; i < body.mappings.length; i += MAX_MAPPING_UPDATES_PER_REQUEST) {
      const chunk = body.mappings.slice(i, i + MAX_MAPPING_UPDATES_PER_REQUEST);
      const updates = chunk.map(toMappingUpdatePayload);
      const result = await this.request<{ updatedCount: number }>(
        this.storePath('/integrations/shipstation/catalog/mappings'),
        { method: 'PUT', body: JSON.stringify({ updates }) },
        true
      );
      updated += result.updatedCount ?? 0;
    }

    return { updated };
  }

  /** Phase 3: Sync one inventory batch (call until hasMore is false). */
  async syncShipStationInventory(
    options?: { offset?: number; batchSize?: number }
  ): Promise<ShipStationInventorySyncSummary> {
    return this.request<ShipStationInventorySyncSummary>(
      this.storePath('/integrations/shipstation/inventory/sync'),
      {
        method: 'POST',
        body: JSON.stringify(options ?? {}),
      },
      true
    );
  }

  /** Phase 3: Sync all sellable mapped inventory (client-driven batching). */
  async syncShipStationInventoryAll(): Promise<ShipStationInventorySyncSummary> {
    let offset = 0;
    let totalSynced = 0;
    let updatedCount = 0;
    let totalAvailable = 0;
    let hasMore = true;

    while (hasMore) {
      const summary = await this.syncShipStationInventory({ offset });
      totalSynced += summary.totalSynced;
      updatedCount += summary.updatedCount;
      totalAvailable = summary.totalAvailable;
      hasMore = summary.hasMore;
      offset = summary.offset;
    }

    return {
      totalSynced,
      updatedCount,
      offset,
      totalAvailable,
      hasMore: false,
    };
  }

  /** Phase 4: Validate shipping addresses via ShipStation V2 proxy. */
  async validateShipStationAddresses(body: {
    addresses: ShipStationValidateAddressInput[];
  }): Promise<{ results: ShipStationAddressValidationResult[] }> {
    return this.request<{ results: ShipStationAddressValidationResult[] }>(
      this.storePath('/integrations/shipstation/fulfillment/validate'),
      { method: 'POST', body: JSON.stringify(body) },
      true
    );
  }

  /** Phase 4: Fetch fulfillment rate quotes for wizard orders. */
  async getShipStationFulfillmentRates(
    body: ShipStationFulfillmentRatesRequest
  ): Promise<ShipStationFulfillmentRatesResponse> {
    return this.request<ShipStationFulfillmentRatesResponse>(
      this.storePath('/integrations/shipstation/fulfillment/rates'),
      { method: 'POST', body: JSON.stringify(body) },
      true
    );
  }

  /** Phase 4: Create a ShipStation fulfillment batch. */
  async createShipStationFulfillmentBatch(
    body: ShipStationCreateFulfillmentBatchRequest
  ): Promise<ShipStationFulfillmentBatchResponse> {
    return this.request<ShipStationFulfillmentBatchResponse>(
      this.storePath('/integrations/shipstation/fulfillment/batches'),
      { method: 'POST', body: JSON.stringify(body) },
      true
    );
  }

  /** Phase 4: Fetch labels for a fulfillment batch. */
  async getShipStationFulfillmentBatchLabels(
    batchId: string
  ): Promise<{ labels: ShipStationFulfillmentLabel[] }> {
    return this.request<{ labels: ShipStationFulfillmentLabel[] }>(
      this.storePath(
        `/integrations/shipstation/fulfillment/batches/${encodeURIComponent(batchId)}/labels`
      ),
      { method: 'GET' },
      true
    );
  }

  /** Phase 4: Create end-of-day manifest for a batch. */
  async createShipStationFulfillmentManifest(
    body: ShipStationCreateManifestRequest
  ): Promise<ShipStationManifestResponse> {
    return this.request<ShipStationManifestResponse>(
      this.storePath('/integrations/shipstation/fulfillment/manifests'),
      { method: 'POST', body: JSON.stringify(body) },
      true
    );
  }

  /** Phase 4: Persist shipped orders with tracking + metadata.shipstation to D1. */
  async completeShipStationFulfillment(
    body: ShipStationFulfillmentCompleteRequest
  ): Promise<ShipStationFulfillmentCompleteResult> {
    return this.request<ShipStationFulfillmentCompleteResult>(
      this.storePath('/integrations/shipstation/fulfillment/complete'),
      { method: 'POST', body: JSON.stringify(body) },
      true
    );
  }

  // Metafields
  async getMetafieldDefinitions(storeId: string): Promise<MetafieldDefinition[]> {
    const data = await this.request<{ definitions?: MetafieldDefinition[] }>(
      `/api/v1/stores/${encodeURIComponent(storeId)}/metafield-definitions`,
      {},
      true
    );
    return data?.definitions ?? [];
  }

  async createMetafieldDefinition(storeId: string, definition: Partial<MetafieldDefinition>): Promise<{ id: string }> {
    return this.request<{ id: string }>(
      `/api/v1/stores/${encodeURIComponent(storeId)}/metafield-definitions`,
      {
        method: 'POST',
        body: JSON.stringify(definition),
      },
      true
    );
  }

  async getEntityMetafields(storeId: string, ownerType: string, ownerId: string): Promise<EntityMetafield[]> {
    const data = await this.request<{ metafields?: EntityMetafield[] }>(
      `/api/v1/stores/${encodeURIComponent(storeId)}/metafields/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}`,
      {},
      true
    );
    return data?.metafields ?? [];
  }

  async upsertEntityMetafield(storeId: string, ownerType: string, ownerId: string, namespace: string, key: string, value: unknown): Promise<void> {
    await this.request<{ message: string }>(
      `/api/v1/stores/${encodeURIComponent(storeId)}/metafields/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ namespace, key, value }),
      },
      true
    );
  }

  async deleteEntityMetafield(storeId: string, ownerType: string, ownerId: string, namespace: string, key: string): Promise<void> {
    await this.request<{ message: string }>(
      `/api/v1/stores/${encodeURIComponent(storeId)}/metafields/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
      {
        method: 'DELETE',
      },
      true
    );
  }
}
